import React, { useMemo, useState, JSX } from 'react';
import moment from 'moment';
import { getCoreRowModel } from '@tanstack/react-table';

// MUI Theme
import Paper from '@mui/material/Paper';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

// Utils
import { FormatForApi, shortDateFormat } from '../../../utils/Format';
import { makeProcessedSightingColumn } from '../../../utils/columns/makeColumn';

// Services
import AuthService from '../../../services/auth.service';
import { exportProcessedSightingByReportId, getProcessedSightingByReportId } from '../../../services/sighting.service';

// Common Components
import { useStandardTable as useTable, Table } from '../../common/table/index';
import SearchBar from '../../common/SearchBar/SearchBar';
import Unauthorized from '../../common/Unauthorized';

// Types
import { Device } from '../../../types/Device';
import { AppPermissions } from '../../../constants/Permissions';
import { FilterableTypes } from '../../../types/FilterableTypes';
import FormInputCollection from '../../common/FormInputCollection/FormInputCollection';

interface RawDataProps {
  device: Device;
  accessKey?: string;
}
export default function RawData(props: RawDataProps): JSX.Element {
  const [lastSeenDateTimeFrom, setLastSeenDateTimeFrom] = useState<Date | null>(null);
  const [lastSeenDateTimeTo, setLastSeenDateTimeTo] = useState<Date | null>(null);
  const [paramFilter, setParamFilter] = useState<any>({
    sortBy: [
      {
        lastSeenDateTime: '-1',
      },
    ],
    filters: [{ deviceId: props.device.deviceId, filterType: FilterableTypes.Equals }],
  });

  const columns = useMemo(() => makeProcessedSightingColumn(true), []);
  const table = useTable<any>({
    columns,
    accessKey: props.accessKey,
    fetchAsync: getProcessedSightingByReportId,
    exportAsync: exportProcessedSightingByReportId,
    params: paramFilter,
    serverSide: true,
  });

  const onSearchChange = (filters: any[]) => {
    const finalFilters = [...filters];

    finalFilters.push({
      deviceId: props.device.deviceId,
      filterType: FilterableTypes.Equals,
    });

    if (lastSeenDateTimeFrom && lastSeenDateTimeTo) {
      finalFilters.push({
        lastSeenDateTime: FormatForApi(moment(lastSeenDateTimeFrom), true),
        filterType: FilterableTypes.GreaterThanOrEqual,
        and: {
          lastSeenDateTime: FormatForApi(moment(lastSeenDateTimeTo), false),
          filterType: FilterableTypes.LessThanOrEqual,
        },
      });
    }

    setParamFilter({
      filters: finalFilters,
    });
  };

  return (
    <React.Fragment>
      {AuthService.hasPermission(AppPermissions.SightingProcessed.Read) ? (
        <>
          <SearchBar
            onSearch={onSearchChange}
            onReset={() => {
              setLastSeenDateTimeFrom(null);
              setLastSeenDateTimeTo(null);
            }}
            columns={columns}
            allowSearchAll={true}
            components={
              <div style={{ display: 'flex', marginBottom: 5 }}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <FormInputCollection label={'Last Seen date'} style={{ marginLeft: '16px', marginBottom: '16px' }}>
                    <div style={{ marginRight: '8px' }}>
                      <DatePicker
                        value={lastSeenDateTimeFrom ? moment(lastSeenDateTimeFrom) : null}
                        label='From'
                        format={shortDateFormat}
                        sx={{ width: '185px' }}
                        slotProps={{ textField: { size: 'small' } }}
                        onChange={(date) => setLastSeenDateTimeFrom(date ? date.toDate() : null)}
                      />
                    </div>
                    <DatePicker
                      value={lastSeenDateTimeTo ? moment(lastSeenDateTimeTo) : null}
                      label='To'
                      format={shortDateFormat}
                      sx={{ width: '185px' }}
                      slotProps={{ textField: { size: 'small' } }}
                      onChange={(date) => setLastSeenDateTimeTo(date ? date.toDate() : null)}
                    />
                  </FormInputCollection>
                </LocalizationProvider>
              </div>
            }
          />
          <Paper>
            <Table {...table} showSearchBar={false} showColumnToggler={false} showTotalPageCount={false} getCoreRowModel={getCoreRowModel()} />
          </Paper>
        </>
      ) : (
        <Unauthorized />
      )}
    </React.Fragment>
  );
}
